import { Fragment, ReactElement, useCallback, useEffect, useState } from "react";

import { Channel, useSendbirdStateContext } from "@sendbird/uikit-react";
import "@sendbird/uikit-react/dist/index.css";
import { v4 as uuidv4 } from "uuid";
import { Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import "./chatStyle.scss";
import { fetchAndSetUpcomingShifts, updateChannel } from "src/modules/chat";
import { logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "src/constants/firebaseEvents";
import { ChatModalProps, ChatProviderProps, SendbirdGroupChannel } from "./chat.types";
import { useQuery } from "@tanstack/react-query";
import { fetchExclusion } from "src/api/workerReview";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { SendbirdChat, Session, SessionType } from "src/modules/interface";
import { useMediaQuery, useTheme } from "@mui/material";
import { CustomTypesChannelFilter, fetchChannels, getMessageInputDefinition } from "./channel";
import SendbirdProvider from "@sendbird/uikit-react/SendbirdProvider";
import { GroupChannelHandler, UnreadChannelFilter } from "@sendbird/chat/groupChannel";
import { BaseChannel } from "@sendbird/chat";
import { SendableMessageType } from "@sendbird/uikit-react/types/utils";
import { FileMessageCreateParams, UserMessageCreateParams } from "@sendbird/chat/message";
import { renderSendbirdChannelMessage } from "./ChannelMessage";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { ChatSearchBar } from "./ChatSearchBar";
import { debounce } from "lodash";
import { ChatHeader } from "./components/ChatHeader";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CircularProgress, Stack } from "@mui/material";
import { ChatChannelTypeFilter, ChatFilterChip } from "./components/ChatChannelTypeFilter";
import { PlacementChatHeader } from "./components/PlacementChatHeader";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";
import { useWorkplaceStarredProfessionals } from "@src/appV2/Professionals/api/starredProfessionals/useWorkplaceStarredProfessionals";
import { useModalState } from "@clipboard-health/ui-react";
import { ManageCredentialsModal } from "@src/appV2/WorkerCredentials/ManageCredentialsModal";
import { ResetCredentialsModal } from "@src/appV2/WorkerCredentials/ResetCredentialsModal";
import { ChannelPreview } from "./ChannelPreview";
import { useChatWorkerQualifications } from "./useChatWorkerQualifications";
import { ChannelMessageWrapper } from "./ChannelMessageWrapper";
import { hasChannelUrl } from "./utils/helpers";

const appId = environmentConfig.REACT_APP_SENDBIRD_APP_ID;

export function ChatPage() {
  const {
    userId,
    user,
    admin,
    profile,
    type: userType,
  } = useSelector<SessionType, Session>((state) => state.session);

  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;

  const history = useHistory();
  const { agentId, facilityId, placementId } = useParams<{
    agentId: string;
    facilityId: string;
    placementId?: string;
  }>();
  const { channels } = useSelector<SessionType, SendbirdChat>((state) => state.chat);
  const [isChannelsLoading, setIsChannelsLoading] = useState<boolean>(false);

  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [unreadChannelsOnly, setUnreadChannelsOnly] = useState<UnreadChannelFilter>(
    UnreadChannelFilter.ALL
  );
  const { isEnabled: isPlacementsEnabled } = useGetPlacementsAccessLevel();
  const [customTypesFilter, setCustomTypesFilter] = useState<CustomTypesChannelFilter>("all");

  const currentChannelUrl = facilityId
    ? [facilityId, agentId, placementId].filter(Boolean).join("_")
    : "";
  const [currentChannel, setCurrentChannel] = useState<SendbirdGroupChannel | undefined>(undefined);
  const manageCredentialsModalState = useModalState();
  const resetCredentialsModalState = useModalState();
  // Redirect logic to ensure admins stay in the correct context
  // When viewing as a facility: redirect if the URL facility doesn't match the current facility context
  // When viewing as an agent: redirect if the URL agent doesn't match the current agent context
  useEffect(() => {
    const isAgentUser = userType === "AGENT";

    if (isAgentUser) {
      // Redirect when viewing as an agent and navigating to a different agent's conversation
      if (isDefined(agentId) && isDefined(userId) && agentId !== userId) {
        history.replace(getLocation("facilityChat"));
      }
    } else {
      // Redirect when viewing as a facility and navigating to a different facility's conversation
      if (isDefined(facilityId) && isDefined(userId) && facilityId !== userId) {
        history.replace(getLocation("facilityChat"));
      }
    }
  }, [history, userId, facilityId, agentId, userType]);

  useEffect(() => {
    if (isDefined(channels) && isDefined(currentChannelUrl)) {
      setCurrentChannel(channels.find((channel) => channel.url === currentChannelUrl));
    }
  }, [setCurrentChannel, channels, currentChannelUrl]);

  const { workplaceStarredProfessionalWorkerIds, isStarredProfessionalEnabled } =
    useWorkplaceStarredProfessionals({
      workplaceId: userId,
    });

  const dispatch = useDispatch();

  const { defaultWorkerQualification, matchingQualifications, lastWorkedShift } =
    useChatWorkerQualifications({
      workerId: agentId,
      workplaceId: facilityId,
      currentChannel,
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchChannels = useCallback(
    debounce(async (props) => {
      try {
        setIsChannelsLoading(true);
        await fetchChannels(props);
      } finally {
        setIsChannelsLoading(false);
      }
    }, 200),
    [fetchChannels]
  );

  useEffect(() => {
    async function fetchAndDispatchChannels() {
      if (sdk && debouncedFetchChannels) {
        debouncedFetchChannels({
          sdk,
          dispatch,
          nicknameContainsFilter: searchQuery,
          customTypesFilter,
          unreadChannelsOnly,
        });
      }
    }
    fetchAndDispatchChannels();
  }, [dispatch, sdk, searchQuery, debouncedFetchChannels, customTypesFilter, unreadChannelsOnly]);

  useEffect(() => {
    if (!admin && facilityId && agentId && user?._id) {
      logEvent(HCF_USER_EVENTS.CHAT_OPENED, {
        location: "chatChannel",
        workerId: agentId,
        workplaceId: facilityId,
        workplaceUserId: user._id,
      });
    }
  }, [facilityId, agentId, user?._id, admin]);

  useEffect(() => {
    if (isDefined(profile?.userId)) {
      setCustomTypesFilter("all");
      setUnreadChannelsOnly(UnreadChannelFilter.ALL);
    }
  }, [profile?.userId]);

  const { isLoading: isLoadingExclusion, data: exclusions } = useQuery(
    ["chat", "exclusion", agentId, facilityId],
    () =>
      fetchExclusion({
        facilityId,
        agentId,
      }),
    {
      enabled: !!(userId && facilityId && agentId),
    }
  );

  const isBlocked = (exclusions?.length ?? 0) > 0;

  const channelClick = (newChannelUrl: string) => {
    const [newFacilityId, newAgentId, newPlacementId] = newChannelUrl.split("_");
    setCurrentChannel(channels.find((channel) => channel.url === newChannelUrl));
    history.push(
      getLocation("facilityChatWithAgent", {
        pathParams: { facilityId: newFacilityId, agentId: newAgentId, placementId: newPlacementId },
      })
    );
  };

  return (
    <Box
      className={"chat-container chat-style"}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box className={"chat " + (currentChannelUrl ? "selected-chat" : "")}>
        <Box className="channels-container" sx={{ overflowY: "scroll" }}>
          <Box sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "white" }}>
            <ChatSearchBar searchQuery={searchQuery ?? ""} setSearchQuery={setSearchQuery} />
            <Stack
              direction="row"
              spacing={1}
              sx={{ padding: 1 }}
              alignItems="center"
              justifyContent="space-between"
            >
              {(isPlacementsEnabled || isStarredProfessionalEnabled) && (
                <ChatChannelTypeFilter
                  customTypesFilter={customTypesFilter}
                  onFilterChange={(filterValue) => {
                    setCustomTypesFilter(filterValue);
                    history.replace(getLocation("facilityChat"));
                  }}
                  isStarredProfessionalEnabled={isStarredProfessionalEnabled}
                  isPlacementsEnabled={isPlacementsEnabled}
                />
              )}
              <ChatFilterChip
                label="Unread"
                isSelected={unreadChannelsOnly === UnreadChannelFilter.UNREAD_MESSAGE}
                onClick={() =>
                  setUnreadChannelsOnly(
                    unreadChannelsOnly === UnreadChannelFilter.ALL
                      ? UnreadChannelFilter.UNREAD_MESSAGE
                      : UnreadChannelFilter.ALL
                  )
                }
              />
            </Stack>
          </Box>
          {isChannelsLoading ? (
            <Stack alignItems="center" height="80%" justifyContent="center">
              <CircularProgress />
            </Stack>
          ) : (
            channels.filter(hasChannelUrl).map((channel, index, array) => {
              const isSelected = channel.url === currentChannelUrl;
              const isFirst = index === 0;
              const isLast = index === array.length - 1;
              return (
                <ChannelPreview
                  key={channel.url}
                  channel={channel}
                  isSelected={isSelected}
                  isFirst={isFirst}
                  isLast={isLast}
                  customTypesFilter={customTypesFilter}
                  userType={userType}
                  currentUserId={sdk.currentUser?.userId}
                  currentChannelUrl={currentChannelUrl}
                  workplaceStarredProfessionalWorkerIds={workplaceStarredProfessionalWorkerIds}
                  handleClick={channelClick}
                  isAdmin={admin}
                />
              );
            })
          )}
        </Box>

        <Channel
          renderMessage={({ message, chainTop, chainBottom }) => (
            <ChannelMessageWrapper message={message}>
              {renderSendbirdChannelMessage({
                message,
                chainTop,
                chainBottom,
                user,
                userId,
                currentChannel,
                channelUrl: currentChannel?.url,
              })}
            </ChannelMessageWrapper>
          )}
          onBeforeSendFileMessage={
            sdk && user
              ? (file, quoteMessage) => handleBeforeSendFileMessage(user, file, quoteMessage, admin)
              : undefined
          }
          onBeforeSendUserMessage={
            sdk && user
              ? (text, quoteMessage) => handleBeforeSendUserMessage(user, text, quoteMessage, admin)
              : undefined
          }
          renderMessageInput={getMessageInputDefinition({
            isLoadingExclusion,
            sdk,
            actionBy: exclusions?.[0]?.actionBy,
            isAdmin: admin,
          })}
          renderCustomSeparator={() => {
            // Setting custom separator as empty object as it is rendered with ChannelMessageWrapper
            return <></>;
          }}
          disableMarkAsRead={admin}
          isReactionEnabled={false}
          channelUrl={currentChannelUrl}
          renderChannelHeader={() =>
            currentChannel?.customType === "placements" ? (
              <PlacementChatHeader
                currentChannel={currentChannel}
                channelUrl={currentChannel.url}
                backAction={true}
              />
            ) : (
              <ChatHeader
                key={`${agentId}_${facilityId}`}
                workerId={agentId}
                workplaceId={facilityId}
                workplaceUserId={user?._id ?? ""}
                currentChannel={currentChannel}
                backAction={true}
                facilityStatus={profile.status}
                isBlocked={isBlocked}
                manageCredentialsModalState={manageCredentialsModalState}
                matchingQualifications={matchingQualifications}
                defaultWorkerQualification={defaultWorkerQualification}
                hasWorkedAtWorkplace={isDefined(lastWorkedShift)}
              />
            )
          }
        />
        {manageCredentialsModalState.modalIsOpen && (
          <ManageCredentialsModal
            onClose={manageCredentialsModalState.closeModal}
            workerName={currentChannel?.metadata?.hcpName}
            onReset={() => {
              resetCredentialsModalState.openModal();
              manageCredentialsModalState.closeModal();
            }}
            workerId={agentId}
            workplaceId={facilityId}
          />
        )}
        {resetCredentialsModalState.modalIsOpen && (
          <ResetCredentialsModal
            onClose={resetCredentialsModalState.closeModal}
            workerName={currentChannel?.metadata?.hcpName}
            onReset={async () => {
              resetCredentialsModalState.closeModal();
            }}
            workerId={agentId}
            workplaceId={facilityId}
          />
        )}
      </Box>
    </Box>
  );
}

export function ChatModal(props: ChatModalProps) {
  const { channelUrl, closeModal } = props;
  const { channels } = useSelector<SessionType, SendbirdChat>((state) => state.chat);
  const { userId, user, profile, admin } = useSelector<SessionType, Session>(
    (state) => state.session
  );
  const currentChannel = channels.find((channel) => channel.url === channelUrl);
  const [workplaceId, workerId] = (channelUrl ?? "").split("_"); // channel's url is the concatenation of workplaceId_workerId and optionally placementId
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;

  const { isLoading: isLoadingExclusion, data: exclusions } = useQuery(
    ["chat", "exclusion", workerId, workplaceId],
    () =>
      fetchExclusion({
        facilityId: workplaceId,
        agentId: workerId,
      }),
    {
      enabled: !!(userId && workerId && workplaceId),
    }
  );

  const isBlocked = (exclusions?.length ?? 0) > 0;

  const { defaultWorkerQualification, matchingQualifications, lastWorkedShift } =
    useChatWorkerQualifications({
      workerId,
      workplaceId,
      currentChannel,
    });

  const theme = useTheme();
  // Anything smaller than 600px wide
  const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down("md")); //

  return (
    <Modal
      className="chat-style"
      footer={<Fragment />}
      visible={Boolean(channelUrl)}
      bodyStyle={{
        height: isTabletOrSmaller ? "100vh" : 688,
        maxHeight: isTabletOrSmaller ? "100vh" : "78vh",
        paddingTop: isTabletOrSmaller ? 0 : "50px",
        padding: isTabletOrSmaller ? 0 : undefined,
      }}
      width={isTabletOrSmaller ? "100vw" : "80vw"}
      style={
        isTabletOrSmaller
          ? {
              top: 0,
              padding: 0,
              maxWidth: "100vw",
            }
          : undefined
      }
      onCancel={() => closeModal()}
    >
      <div className="chat-container chat-style">
        <Channel
          renderMessage={({ message, chainTop, chainBottom }) =>
            renderSendbirdChannelMessage({
              message,
              chainTop,
              chainBottom,
              user,
              userId,
              currentChannel,
              channelUrl: currentChannel?.url,
            })
          }
          renderMessageInput={getMessageInputDefinition({
            isLoadingExclusion,
            sdk,
            actionBy: exclusions?.[0]?.actionBy,
            isAdmin: admin,
          })}
          onBeforeSendFileMessage={
            sdk && user
              ? (file, quoteMessage) => handleBeforeSendFileMessage(user, file, quoteMessage, admin)
              : undefined
          }
          onBeforeSendUserMessage={
            sdk && user
              ? (text, quoteMessage) => handleBeforeSendUserMessage(user, text, quoteMessage, admin)
              : undefined
          }
          disableMarkAsRead={admin}
          isReactionEnabled={!admin}
          channelUrl={channelUrl}
          renderChannelHeader={() =>
            currentChannel?.customType === "placements" ? (
              <PlacementChatHeader
                currentChannel={currentChannel}
                channelUrl={currentChannel.url}
                backAction={false}
              />
            ) : (
              <ChatHeader
                key={`${workerId}_${workplaceId}`}
                workerId={workerId}
                workplaceId={userId}
                workplaceUserId={user?._id ?? ""}
                currentChannel={{
                  name: currentChannel?.name ?? "Channel",
                  customType: currentChannel?.customType ?? "",
                  shift: currentChannel?.shift,
                  metadata: {
                    ...currentChannel?.metadata,
                  },
                }}
                backAction={false}
                facilityStatus={profile.status}
                isBlocked={isBlocked}
                matchingQualifications={matchingQualifications}
                defaultWorkerQualification={defaultWorkerQualification}
                hasWorkedAtWorkplace={isDefined(lastWorkedShift)}
              />
            )
          }
        />
      </div>
    </Modal>
  );
}

export function ChatProvider(props: ChatProviderProps) {
  const { children } = props;
  const userId = useSelector<SessionType, string | undefined>((store) => store.session.userId);
  const session = useSelector<SessionType, Session>((store) => store.session);
  const sendBirdAccessToken = useSelector<SessionType, string | undefined>(
    (store) => store.session.sendBirdAccessToken
  );
  let sendbirdAppId = appId;
  // This key is used to force re-mount when a user changes the selected workplace
  const sendbirdKey = `${userId}-${sendBirdAccessToken}`;

  if (!session.profile || session.user?.email.includes("@playwright-hcf.com")) {
    sendbirdAppId = "";
  }

  return (
    <SendbirdProvider
      key={sendbirdKey}
      appId={sendbirdAppId ?? ""}
      userId={userId ?? ""}
      nickname={session.profile?.name}
      accessToken={sendBirdAccessToken}
    >
      <ChatProviderWithStore>{children}</ChatProviderWithStore>
    </SendbirdProvider>
  );
}

function ChatProviderWithStore(props: ChatProviderProps): ReactElement {
  const { children } = props;
  const dispatch = useDispatch();
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;
  const isSendbirdConcurrentConnectionOptimizationEnabled = useCbhFlag(
    CbhFeatureFlag.SENDBIRD_CONCURRENT_CONNECTION_OPTIMIZATION,
    { defaultValue: false }
  );

  useEffect(() => {
    if (!sdk?.groupChannel) {
      return;
    }

    const uuid = uuidv4();

    const channelHandler = new GroupChannelHandler({
      onChannelChanged: (channel) => {
        if (!channel.cachedMetaData || Object.keys(channel.cachedMetaData).length === 0) {
          // sendbird will auto fetch a channel when one is created, however it will not include the metadata
          // so we need to refetch the channels to get the metadata
          fetchChannels({ sdk, dispatch });
        } else {
          updateChannel(dispatch, channel);
        }
      },
      onMessageReceived: (_channel: BaseChannel, message) => {
        if (message.isAdminMessage()) {
          fetchAndSetUpcomingShifts(dispatch);
        }
      },
    });

    channelHandler.onUserReceivedInvitation = (channel) => {
      updateChannel(dispatch, channel);
      fetchAndSetUpcomingShifts(dispatch);
    };

    sdk.groupChannel.addGroupChannelHandler(uuid, channelHandler);

    return () => sdk?.groupChannel.removeGroupChannelHandler?.(uuid);
  }, [dispatch, sdk]);

  useEffect(() => {
    // Exit early and avoid adding listeners if the feature flag is turned off
    if (!isSendbirdConcurrentConnectionOptimizationEnabled) {
      return () => null;
    }

    const handleTabChange = async () => {
      if (document.visibilityState === "visible") {
        sdk?.setForegroundState?.();
        await fetchChannels({ sdk, dispatch });
      } else {
        sdk?.setBackgroundState?.();
      }
    };

    const handleWindowChange = async () => {
      if (!document.hasFocus() && sdk?.connectionState === "OPEN") {
        sdk?.setBackgroundState?.();
      }

      if (document.hasFocus() && sdk?.connectionState === "CLOSED") {
        sdk?.setForegroundState?.();
        await fetchChannels({ sdk, dispatch });
      }
    };

    // This is fired when user switches tabs
    // Internally, we check if the tab is focused, then reconnect, otherwise we disconnect
    document.addEventListener("visibilitychange", handleTabChange);

    // Moving away from current window and coming back to it is tracked via blur and focus events
    // Internally, we check if the tab is focused as the window gets focus back and reconnect, otherwise we disconnect
    window.addEventListener("blur", handleWindowChange);
    window.addEventListener("focus", handleWindowChange);

    return () => {
      document.removeEventListener("visibilitychange", handleTabChange);
      window.removeEventListener("blur", handleWindowChange);
      window.removeEventListener("focus", handleWindowChange);
    };
  }, [dispatch, isSendbirdConcurrentConnectionOptimizationEnabled, sdk]);

  return children;
}

function handleBeforeSendUserMessage(
  user: {
    access: string[];
    _id: string;
    email: string;
    name: string;
    permissions?: string[] | undefined;
  },
  text: string,
  quoteMessage?: SendableMessageType,
  isAdmin?: boolean
): UserMessageCreateParams {
  if (isAdmin) {
    throw new Error("Admin users cannot send messages in chat.");
  }

  const message = typeof text === "string" ? text.trim() : text;
  const params: UserMessageCreateParams = {
    message,
    data: JSON.stringify({
      workplaceUserId: user._id,
      workplaceUserName: user.name,
    }),
  };

  if (quoteMessage) {
    params.isReplyToChannel = true;
    params.parentMessageId = quoteMessage.messageId;
  }

  return params;
}

function handleBeforeSendFileMessage(
  user: {
    access: string[];
    _id: string;
    email: string;
    permissions?: string[] | undefined;
    name: string;
  },
  file: File,
  quoteMessage?: SendableMessageType,
  isAdmin?: boolean
): FileMessageCreateParams {
  if (isAdmin) {
    throw new Error("Admin users cannot send messages in chat.");
  }

  const params: FileMessageCreateParams = {
    file,
    data: JSON.stringify({
      workplaceUserId: user._id,
      workplaceUserName: user.name,
    }),
  };

  if (quoteMessage) {
    params.isReplyToChannel = true;
    params.parentMessageId = quoteMessage.messageId;
  }

  return params;
}
